import { useState, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import axiosInstance from "../utils/axiosConfig";
import { FaSync, FaCheck, FaTimes, FaSearch, FaSpinner, FaUserTie, FaUserGraduate, FaBook, FaClipboardCheck, FaExclamationCircle, FaCheckCircle, FaTimesCircle, FaArrowRight, FaListAlt, FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";

const HODDashboard = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [examRequests, setExamRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [adminComment, setAdminComment] = useState("");
  const [processingType, setProcessingType] = useState(null);
  const [showExamReviewModal, setShowExamReviewModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [userDepartment, setUserDepartment] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalExams: 0,
    pendingRequests: 0,
    approvedExams: 0,
    rejectedExams: 0
  });

  useEffect(() => {
    // First fetch the HOD's department
    fetchHODDepartment();
  }, []);

  useEffect(() => {
    // Fetch exam requests once we have the department
    if (userDepartment) {
      fetchExamRequests();
    }
  }, [userDepartment]);

  useEffect(() => {
    // Update dashboard stats whenever exam requests change
    if (examRequests.length > 0) {
      updateDashboardStats();
    }
  }, [examRequests]);

  const updateDashboardStats = () => {
    setDashboardStats({
      totalExams: examRequests.length,
      pendingRequests: examRequests.filter(req => req.status === 'pending').length,
      approvedExams: examRequests.filter(req => req.status === 'approved').length,
      rejectedExams: examRequests.filter(req => req.status === 'rejected').length
    });
  };

  const fetchDashboardStats = async () => {
    // This has been replaced by the updateDashboardStats function above
  };

  const fetchHODDepartment = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/users/check-hod");
      if (response.data && response.data.department) {
        setUserDepartment(response.data.department);
        console.log("HOD Department:", response.data.department);
      } else {
        toast.error("Could not determine your department");
      }
    } catch (error) {
      console.error("Error fetching HOD profile:", error);
      toast.error("Failed to load your department information");
    } finally {
      setLoading(false);
    }
  };

  const fetchExamRequests = async () => {
    try {
      setLoading(true);
      // Use the department ID to filter requests
      const response = await axiosInstance.get(`/api/exam-requests?department=${userDepartment._id}`);
      
      if (response.data) {
        // Process the data to ensure proper subject information
        const processedRequests = response.data.map(request => {
          // Log each request to inspect data structure
          console.log('Request subject data:', request.subject);
          
          // Check different possible formats of subject data
          let subjectName = 'N/A';
          
          if (request.subject && typeof request.subject === 'object' && request.subject.name) {
            // If subject is an object with name property
            subjectName = request.subject.name;
          } else if (request.subjectName) {
            // If there's a dedicated subjectName field
            subjectName = request.subjectName;
          } else if (request.subject && typeof request.subject === 'string') {
            // If subject is just a string (possibly just the ID)
            subjectName = request.subject;
          } else if (request.subjectData && request.subjectData.name) {
            // Another possible format
            subjectName = request.subjectData.name;
          }
          
          return {
            ...request,
            subject: {
              _id: request.subject?._id || (typeof request.subject === 'string' ? request.subject : null),
              name: subjectName
            }
          };
        });
        
        setExamRequests(processedRequests);
        console.log("Department exam requests:", processedRequests);
      }
    } catch (error) {
      console.error("Error fetching exam requests:", error);
      toast.error(error.response?.data?.message || "Failed to fetch exam requests");
      setError(error.response?.data?.message || "Failed to fetch exam requests");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setAdminComment('Approved by department head');
    setShowModal(true);
    setProcessingType('approve');
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setAdminComment('');
    setShowModal(true);
    setProcessingType('reject');
  };

  const handleExamReview = async (request) => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/exam-requests/${request._id}`
      );
      
      const data = response.data;
      console.log("Review data:", data); // Log the full review data to inspect
      
      setReviewData(data);
      setSelectedRequest(request);
      setShowExamReviewModal(true);
    } catch (error) {
      console.error('Error fetching exam details:', error);
      toast.error('Failed to load exam details for review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestStatusUpdate = async () => {  
    if (!selectedRequest) return;

    setActionLoading(true);
    const status = processingType === 'approve' ? 'approved' : 'rejected';
    setProcessingStatus(status === 'approved' ? 'Processing approval...' : 'Processing request...');

    try {
      const response = await axiosInstance.put(
        `/api/exam-requests/${selectedRequest._id}`,
        {
          status,
          adminComment
        }
      );

      if (response.data) {
        setExamRequests(examRequests.map(req =>
          req._id === selectedRequest._id
            ? { ...req, status: response.data.status }
            : req
        ));

        toast.success(`Request ${status} successfully`);
        await fetchExamRequests();
        setShowModal(false);
        setSelectedRequest(null);
        setAdminComment('');
        setProcessingType(null);
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.response?.data?.message || `Failed to ${status} request`);
    } finally {
      setActionLoading(false);
      setProcessingStatus('');
    }
  };

  const refreshData = async () => {
    try {
      setIsRefreshing(true);
      if (userDepartment) {
        await fetchExamRequests();
        toast.success('Data refreshed successfully');
      } else {
        toast.error('Department information not available');
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data");
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return isDarkMode 
          ? 'bg-green-900 text-green-200' 
          : 'bg-green-100 text-green-800';
      case 'rejected':
        return isDarkMode 
          ? 'bg-red-900 text-red-200' 
          : 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return isDarkMode 
          ? 'bg-yellow-900 text-yellow-200' 
          : 'bg-yellow-100 text-yellow-800';
    }
  };

  // Filter requests based on search query
  const getFilteredRequests = () => {
    if (!searchQuery.trim()) return examRequests;
    
    // Handle filter buttons
    if (searchQuery.toLowerCase() === "pending") {
      return examRequests.filter(request => request.status === 'pending');
    }
    if (searchQuery.toLowerCase() === "approved") {
      return examRequests.filter(request => request.status === 'approved');
    }
    if (searchQuery.toLowerCase() === "rejected") {
      return examRequests.filter(request => request.status === 'rejected');
    }
    
    // Handle text search
    return examRequests.filter(request => 
      request.examName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.subject?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.submittedBy?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.status?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <div className={`min-h-screen pt-16 pb-12 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 md:px-6 mt-8">
        {/* Dashboard Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {userDepartment ? `${userDepartment.name} Dashboard` : 'Department Dashboard'}
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Welcome back! Here's an overview of exam requests in your department.
          </p>
        </div>

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Exams */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className={`rounded-lg shadow-md p-6 border-l-4 border-indigo-500 ${
              isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Exams</p>
                <p className="text-2xl font-bold mt-1">{dashboardStats.totalExams}</p>
              </div>
              <div className={`p-3 rounded-full ${
                isDarkMode ? 'bg-indigo-900/20 text-indigo-400' : 'bg-indigo-100 text-indigo-500'
              }`}>
                <FaListAlt className="text-xl" />
              </div>
            </div>
            <div className="mt-4">
              <button 
                onClick={() => setSearchQuery("")}
                className={`text-sm flex items-center ${
                isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'
              }`}>
                View All Exams <FaArrowRight className="ml-1 text-xs" />
              </button>
            </div>
          </motion.div>

          {/* Pending Requests */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className={`rounded-lg shadow-md p-6 border-l-4 border-amber-500 ${
              isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pending Requests</p>
                <p className="text-2xl font-bold mt-1">{dashboardStats.pendingRequests}</p>
              </div>
              <div className={`p-3 rounded-full ${
                isDarkMode ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-100 text-amber-500'
              }`}>
                <FaClipboardCheck className="text-xl" />
              </div>
            </div>
            <div className="mt-4">
              <button 
                onClick={() => setSearchQuery("pending")}
                className={`text-sm flex items-center ${
                isDarkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700'
              }`}>
                View Pending <FaArrowRight className="ml-1 text-xs" />
              </button>
            </div>
          </motion.div>

          {/* Approved Exams */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className={`rounded-lg shadow-md p-6 border-l-4 border-green-500 ${
              isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Approved Exams</p>
                <p className="text-2xl font-bold mt-1">{dashboardStats.approvedExams}</p>
              </div>
              <div className={`p-3 rounded-full ${
                isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-500'
              }`}>
                <FaThumbsUp className="text-xl" />
              </div>
            </div>
            <div className="mt-4">
              <button 
                onClick={() => setSearchQuery("approved")}
                className={`text-sm flex items-center ${
                isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'
              }`}>
                View Approved <FaArrowRight className="ml-1 text-xs" />
              </button>
            </div>
          </motion.div>

          {/* Rejected Exams */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className={`rounded-lg shadow-md p-6 border-l-4 border-red-500 ${
              isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rejected Exams</p>
                <p className="text-2xl font-bold mt-1">{dashboardStats.rejectedExams}</p>
              </div>
              <div className={`p-3 rounded-full ${
                isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-500'
              }`}>
                <FaThumbsDown className="text-xl" />
              </div>
            </div>
            <div className="mt-4">
              <button 
                onClick={() => setSearchQuery("rejected")}
                className={`text-sm flex items-center ${
                isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
              }`}>
                View Rejected <FaArrowRight className="ml-1 text-xs" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Exam Requests Section */}
        <div className="mb-8">
          <div className={`rounded-lg shadow-md overflow-hidden ${
            isDarkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b gap-4 ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }">
              <h2 className={`text-xl font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>
                Exam Requests
                {searchQuery === "pending" && <span className="ml-2 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Pending</span>}
                {searchQuery === "approved" && <span className="ml-2 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Approved</span>}
                {searchQuery === "rejected" && <span className="ml-2 text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">Rejected</span>}
              </h2>
              
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-grow sm:flex-grow-0 sm:w-64">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search requests..."
                    className={`pl-10 pr-4 py-2 w-full rounded-lg text-sm ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } border focus:outline-none focus:ring-2 ${
                      isDarkMode ? 'focus:ring-blue-500/50' : 'focus:ring-blue-500/50'
                    }`}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className={`h-4 w-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} hover:text-gray-700`}>×</span>
                    </button>
                  )}
                </div>
                
                {/* Filter buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSearchQuery("")}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      !searchQuery
                        ? isDarkMode ? 'bg-blue-500 text-white' : 'bg-blue-500 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setSearchQuery("pending")}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      searchQuery === "pending"
                        ? isDarkMode ? 'bg-amber-500 text-white' : 'bg-amber-500 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => setSearchQuery("approved")}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      searchQuery === "approved"
                        ? isDarkMode ? 'bg-green-500 text-white' : 'bg-green-500 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Approved
                  </button>
                  <button
                    onClick={() => setSearchQuery("rejected")}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      searchQuery === "rejected"
                        ? isDarkMode ? 'bg-red-500 text-white' : 'bg-red-500 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    Rejected
                  </button>
                </div>
                
                <button
                  onClick={refreshData}
                  disabled={isRefreshing}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Refresh requests"
                >
                  <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="p-8 flex justify-center">
                <Loader size="medium" text="Loading requests..." />
              </div>
            ) : error ? (
              <div className={`text-center p-6 ${
                isDarkMode ? 'text-red-400' : 'text-red-500'
              }`}>{error}</div>
            ) : getFilteredRequests().length === 0 ? (
              <div className="p-8 text-center">
                <div className={`mx-auto w-16 h-16 flex items-center justify-center rounded-full mb-4 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  <FaClipboardCheck className={`w-8 h-8 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                </div>
                <h3 className={`text-lg font-medium mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}>No requests found</h3>
                <p className={`${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {searchQuery 
                    ? "Try a different search term or filter" 
                    : "There are no exam requests at the moment"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y ${
                  isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
                }">
                  <thead className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <tr>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Exam Name
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Faculty
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Status
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Date
                      </th>
                      <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${
                    isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
                  }`}>
                    {getFilteredRequests().map((request) => (
                      <tr key={request._id} className={
                        isDarkMode 
                          ? 'hover:bg-gray-700 text-gray-200' 
                          : 'hover:bg-gray-50'
                      }>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                              request.status === 'approved' 
                                ? isDarkMode ? 'bg-green-900/20 text-green-500' : 'bg-green-100 text-green-600'
                                : request.status === 'rejected'
                                ? isDarkMode ? 'bg-red-900/20 text-red-500' : 'bg-red-100 text-red-600'
                                : isDarkMode ? 'bg-blue-900/20 text-blue-500' : 'bg-blue-100 text-blue-600'
                            }`}>
                              {request.status === 'approved' ? (
                                <FaCheckCircle />
                              ) : request.status === 'rejected' ? (
                                <FaTimesCircle />
                              ) : (
                                <FaExclamationCircle />
                              )}
                            </div>
                            <div className="ml-4">
                              <div className={`text-sm font-medium ${
                                isDarkMode ? 'text-white' : 'text-gray-900'
                              }`}>
                                {request.examName || "Untitled Exam"}
                              </div>
                              <div className={`text-sm ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {request.totalQuestions ? `${request.totalQuestions} questions` : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {request.submittedBy?.name || "Unknown Faculty"}
                          </div>
                          <div className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {request.submittedBy?.email || "No email"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            request.status === 'approved'
                              ? isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                              : request.status === 'rejected'
                              ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
                              : isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {request.status === 'approved' ? 'Approved' : 
                             request.status === 'rejected' ? 'Rejected' : 
                             'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {new Date(request.createdAt).toLocaleDateString()}
                          </div>
                          <div className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {new Date(request.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {request.status === 'pending' ? (
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <button
                                onClick={() => handleExamReview(request)}
                                className={`px-2 py-1 rounded font-medium text-xs flex items-center justify-center ${
                                  isDarkMode 
                                    ? 'bg-blue-900/20 text-blue-400 hover:bg-blue-900/30' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                              >
                                <span className="flex items-center">
                                  <FaSearch className="mr-1" /> Review
                                </span>
                              </button>
                              <button
                                onClick={() => handleApprove(request)}
                                className={`px-2 py-1 rounded font-medium text-xs flex items-center justify-center ${
                                  isDarkMode 
                                    ? 'bg-green-900/20 text-green-400 hover:bg-green-900/30' 
                                    : 'bg-green-100 text-green-600 hover:bg-green-200'
                                }`}
                              >
                                <span className="flex items-center">
                                  <FaCheck className="mr-1" /> Approve
                                </span>
                              </button>
                              <button
                                onClick={() => handleReject(request)}
                                className={`px-2 py-1 rounded font-medium text-xs flex items-center justify-center ${
                                  isDarkMode 
                                    ? 'bg-red-900/20 text-red-400 hover:bg-red-900/30' 
                                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                                }`}
                              >
                                <span className="flex items-center">
                                  <FaTimes className="mr-1" /> Reject
                                </span>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleExamReview(request)}
                              className={`px-3 py-1 rounded text-xs ${
                                isDarkMode
                                  ? 'bg-indigo-900/20 text-indigo-400 hover:bg-indigo-900/30'
                                  : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                              }`}
                            >
                              View Details
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Review Exam Modal */}
        {showExamReviewModal && reviewData && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-4xl w-full ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-white text-gray-900'
                } max-h-[90vh] overflow-y-auto`}
              >
                <div className={`flex justify-between items-center p-6 border-b ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-200'
                } sticky top-0 z-10 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <h3 className="text-xl font-semibold">
                    Reviewing: {selectedRequest?.examName}
                  </h3>
                  <button
                    onClick={() => setShowExamReviewModal(false)}
                    className={`rounded-md p-1 inline-flex items-center justify-center ${
                      isDarkMode 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                        : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
                    } focus:outline-none`}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="p-6">
                  {/* Status Badge */}
                  <div className={`mb-6 p-4 rounded-lg ${
                    selectedRequest?.status === 'approved'
                      ? isDarkMode ? 'bg-green-900/20 border border-green-900/30' : 'bg-green-50 border border-green-100'
                      : selectedRequest?.status === 'rejected'
                      ? isDarkMode ? 'bg-red-900/20 border border-red-900/30' : 'bg-red-50 border border-red-100'
                      : isDarkMode ? 'bg-yellow-900/20 border border-yellow-900/30' : 'bg-yellow-50 border border-yellow-100'
                  }`}>
                    <div className="flex items-center">
                      <div className={`rounded-full p-2 mr-3 ${
                        selectedRequest?.status === 'approved'
                          ? isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                          : selectedRequest?.status === 'rejected'
                          ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-600'
                          : isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'
                      }`}>
                        {selectedRequest?.status === 'approved' ? (
                          <FaCheckCircle className="w-5 h-5" />
                        ) : selectedRequest?.status === 'rejected' ? (
                          <FaTimesCircle className="w-5 h-5" />
                        ) : (
                          <FaExclamationCircle className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className={`text-lg font-medium ${
                          selectedRequest?.status === 'approved'
                            ? isDarkMode ? 'text-green-400' : 'text-green-800'
                            : selectedRequest?.status === 'rejected'
                            ? isDarkMode ? 'text-red-400' : 'text-red-800'
                            : isDarkMode ? 'text-yellow-400' : 'text-yellow-800'
                        }`}>
                          Status: {selectedRequest?.status === 'approved' ? 'Approved' : 
                                 selectedRequest?.status === 'rejected' ? 'Rejected' : 
                                 'Pending Review'}
                        </p>
                        {selectedRequest?.status === 'rejected' && selectedRequest.adminComment && (
                          <p className={`mt-1 text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            Reason: {selectedRequest.adminComment}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Exam Info Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className={`rounded-lg p-5 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`text-lg font-medium mb-4 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-800'
                      }`}>
                        Exam Details
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>Exam Name:</span>
                          <span className="font-medium">{reviewData.examName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>Subject:</span>
                          <span className="font-medium">{reviewData.subject?.name || "Not specified"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>Total Questions:</span>
                          <span className="font-medium">{reviewData.totalQuestions || reviewData.questions?.length || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className={`rounded-lg p-5 ${
                      isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                    }`}>
                      <h4 className={`text-lg font-medium mb-4 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-800'
                      }`}>
                        Exam Configuration
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>Duration:</span>
                          <span className="font-medium">
                            {reviewData.duration || reviewData.timeLimit || reviewData.examTime || "60"} minutes
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>Passing Marks:</span>
                          <span className="font-medium">
                            {reviewData.passingMarks || (reviewData.passingPercentage ? `${reviewData.passingPercentage}%` : "Not specified")}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className={`text-sm ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>Created On:</span>
                          <span className="font-medium">
                            {new Date(reviewData.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Question Summary */}
                  <div className="mb-6">
                    <h4 className={`text-lg font-medium mb-4 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      Question Type Summary
                    </h4>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className={`p-4 rounded-lg text-center ${
                        isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'
                      }`}>
                        <p className={`text-sm mb-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>Total Questions</p>
                        <p className={`text-2xl font-bold ${
                          isDarkMode ? 'text-blue-400' : 'text-blue-600'
                        }`}>
                          {reviewData.questions?.length || 0}
                        </p>
                      </div>
                      
                      <div className={`p-4 rounded-lg text-center ${
                        isDarkMode ? 'bg-green-900/20' : 'bg-green-50'
                      }`}>
                        <p className={`text-sm mb-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>MCQ</p>
                        <p className={`text-2xl font-bold ${
                          isDarkMode ? 'text-green-400' : 'text-green-600'
                        }`}>
                          {reviewData.questions?.filter(q => 
                            q.type === "MCQ" || 
                            q.type === "mcq" || 
                            (q.options && q.options.length > 0)
                          ).length || 0}
                        </p>
                      </div>
                      
                      <div className={`p-4 rounded-lg text-center ${
                        isDarkMode ? 'bg-amber-900/20' : 'bg-amber-50'
                      }`}>
                        <p className={`text-sm mb-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>True/False</p>
                        <p className={`text-2xl font-bold ${
                          isDarkMode ? 'text-amber-400' : 'text-amber-600'
                        }`}>
                          {reviewData.questions?.filter(q => 
                            q.type === "TrueFalse" || 
                            q.type === "truefalse" || 
                            q.type === "true_false"
                          ).length || 0}
                        </p>
                      </div>
                      
                      <div className={`p-4 rounded-lg text-center ${
                        isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'
                      }`}>
                        <p className={`text-sm mb-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>Descriptive</p>
                        <p className={`text-2xl font-bold ${
                          isDarkMode ? 'text-purple-400' : 'text-purple-600'
                        }`}>
                          {reviewData.questions?.filter(q => 
                            q.type === "Descriptive" || 
                            q.type === "descriptive" || 
                            q.type === "text" ||
                            (!q.options || q.options.length === 0)
                          ).length || 0}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Preview of first few questions */}
                  {reviewData.questions && reviewData.questions.length > 0 && (
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className={`text-lg font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-800'
                        }`}>
                          Question Preview
                        </h4>
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Showing all {reviewData.questions.length} questions
                        </span>
                      </div>
                      
                      <div className="space-y-4">
                        {reviewData.questions.map((question, index) => (
                          <div key={index} className={`p-4 rounded-lg border ${
                            isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex">
                              <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full mr-3 ${
                                isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {index + 1}
                              </span>
                              <div>
                                <p className="font-medium">
                                  {question.text || question.question || "Question text not available"}
                                </p>
                                {question.options && question.options.length > 0 && (
                                  <div className="mt-3 space-y-2">
                                    {question.options.map((option, optIndex) => (
                                      <div key={optIndex} className="flex items-center">
                                        <span className={`w-6 h-6 flex items-center justify-center rounded-full mr-2 text-xs ${
                                          (Array.isArray(question.correctAnswer) && question.correctAnswer.includes(optIndex + 1)) ||
                                          question.correctAnswer === optIndex + 1 ||
                                          question.correctAnswer === option ||
                                          (typeof option === 'object' && option.isCorrect)
                                            ? isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                                            : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                                        }`}>
                                          {String.fromCharCode(65 + optIndex)}
                                        </span>
                                        <span className={`${
                                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                          {typeof option === 'object' ? option.text : option}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className={`sticky bottom-0 px-6 py-4 flex justify-end space-x-4 border-t ${
                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <button
                    onClick={() => setShowExamReviewModal(false)}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Close
                  </button>
                  {selectedRequest?.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          setShowExamReviewModal(false);
                          handleReject(selectedRequest);
                        }}
                        className={`px-4 py-2 rounded-lg font-medium ${
                          isDarkMode 
                            ? 'bg-red-500 bg-opacity-20 text-red-400 hover:bg-opacity-30' 
                            : 'bg-red-500 text-white hover:bg-red-600'
                        }`}
                      >
                        <span className="flex items-center">
                          <FaTimes className="mr-2" /> Reject
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setShowExamReviewModal(false);
                          handleApprove(selectedRequest);
                        }}
                        className={`px-4 py-2 rounded-lg font-medium ${
                          isDarkMode 
                            ? 'bg-green-500 bg-opacity-20 text-green-400 hover:bg-opacity-30' 
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        <span className="flex items-center">
                          <FaCheck className="mr-2" /> Approve
                        </span>
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        )}
        
        {/* Approval/Rejection Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`rounded-lg overflow-hidden shadow-xl w-full max-w-lg ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white' 
                    : 'bg-white text-gray-900'
                }`}
              >
                <div className={`flex justify-between items-center p-6 border-b ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <h3 className="text-lg font-medium">
                    {processingType === 'approve' ? 'Approve' : 'Reject'} Exam Request
                  </h3>
                  {!actionLoading && (
                    <button
                      onClick={() => setShowModal(false)}
                      className={`rounded-md p-1 inline-flex items-center justify-center ${
                        isDarkMode 
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                          : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100'
                      } focus:outline-none`}
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Comment {processingType === 'reject' ? '(Required)' : '(Optional)'}
                    </label>
                    <textarea
                      rows={3}
                      value={adminComment}
                      onChange={(e) => setAdminComment(e.target.value)}
                      disabled={actionLoading}
                      placeholder={processingType === 'approve' 
                        ? "Add any comments for faculty (optional)" 
                        : "Please provide a reason for rejection"
                      }
                      className={`w-full px-3 py-2 rounded-lg shadow-sm ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } border focus:ring-2 focus:ring-offset-2 ${
                        processingType === 'approve'
                          ? isDarkMode ? 'focus:ring-green-500/50' : 'focus:ring-green-500/50'
                          : isDarkMode ? 'focus:ring-red-500/50' : 'focus:ring-red-500/50'
                      }`}
                    />
                  </div>

                  {actionLoading && (
                    <div className="flex items-center justify-center mt-4">
                      <div className={`flex items-center space-x-2 ${
                        processingType === 'approve'
                          ? isDarkMode ? 'text-green-400' : 'text-green-600'
                          : isDarkMode ? 'text-red-400' : 'text-red-600'
                      }`}>
                        <FaSpinner className="w-5 h-5 animate-spin" />
                        <span>{processingStatus}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className={`px-6 py-4 sm:px-6 sm:flex sm:flex-row-reverse bg-opacity-50 ${
                  isDarkMode ? 'bg-gray-700 bg-opacity-50' : 'bg-gray-50'
                }`}>
                  <button
                    onClick={handleRequestStatusUpdate}
                    disabled={actionLoading || (processingType === 'reject' && !adminComment.trim())}
                    className={`w-full sm:w-auto sm:ml-3 mb-3 sm:mb-0 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                      processingType === 'approve'
                        ? isDarkMode 
                          ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500 text-white' 
                          : 'bg-green-600 hover:bg-green-700 focus:ring-green-500 text-white'
                        : isDarkMode 
                          ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white' 
                          : 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white'
                    } ${(actionLoading || (processingType === 'reject' && !adminComment.trim())) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {processingType === 'approve' ? 'Approve Request' : 'Reject Request'}
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    disabled={actionLoading}
                    className={`w-full sm:w-auto inline-flex justify-center rounded-md border px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                      isDarkMode 
                        ? 'border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-300 focus:ring-gray-500' 
                        : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-gray-500'
                    } ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HODDashboard;